import { Tooltip, Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import Pagination from '../../../../components/FlowBuilder/Common/Pagination/index.tsx';
import { userInformationAtom } from '../../../../Services/_states/auth.js';
import { calculatePercentageOfTotal } from '../../utils/templateConstants';
import { getUserSummaryDownloadUrl, TEMPLATE_BASE_PATH } from '../../constants/templateApi';
import { formatReportingDate, USE_WHATSAPP_REPORTING } from '../../constants/reportingConfig';
import { templateReportingService } from '../../services/templateReportingService';
import TemplateDataTable from '../shared/TemplateDataTable';
import InsightsRowActions from './InsightsRowActions';
import { useTemplateDetailsContext } from '../../context/TemplateDetailsContext';
import { getTemplateButtonColumnDefs } from '../../utils/templateButtonColumns';

function formatRate(value) {
  if (value == null || Number.isNaN(Number(value))) return '0%';
  return `${Number(value).toFixed(2)}%`;
}

export default function TemplateSendsPanel() {
  const navigate = useNavigate();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const companyId = loggedInUser?.profile?.organisation?.organisation_id;
  const [sorting, setSorting] = useState([]);

  const {
    isOrgMode,
    templateName,
    botNumber,
    filters,
    paginatedSummaryRows,
    totalSummaryCount,
    isLoading,
    updateFilters,
    handleSortingChange,
    navigateToTemplate,
    template,
  } = useTemplateDetailsContext();

  const buttonColumnDefs = useMemo(
    () => getTemplateButtonColumnDefs(template),
    [template]
  );

  const handleChangePage = useCallback(
    (_event, newPage) => {
      updateFilters({ page: newPage });
    },
    [updateFilters]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const limit = parseInt(event.target.value, 10) || 10;
      updateFilters({ limit, page: 0 });
    },
    [updateFilters]
  );

  const navigateToUsers = useCallback(
    (row) => {
      const sentDate = formatReportingDate(row?.sent_date) || row?.sent_date || '';
      navigate(
        `${TEMPLATE_BASE_PATH}/template-users/${templateName}/${row?.template_id}/${sentDate}?bot=${encodeURIComponent(botNumber || '')}`
      );
    },
    [navigate, templateName, botNumber]
  );

  const handleDownloadRecipients = useCallback(
    async (row) => {
      const sentDate = formatReportingDate(row?.sent_date) || row?.sent_date;
      if (!row?.template_id || !sentDate || !companyId) return;
      if (USE_WHATSAPP_REPORTING) {
        await templateReportingService.exportRecipients(
          row.template_id,
          {
            company_id: companyId,
            bot_number: botNumber,
            sent_date: sentDate,
          },
          `template_users_${sentDate}.csv`
        );
        return;
      }
      window.open(getUserSummaryDownloadUrl(row.template_id, row.sent_date), '_blank', 'noopener,noreferrer');
    },
    [botNumber, companyId]
  );

  const orgColumns = useMemo(
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => filters.page * filters.limit + row.index + 1,
      },
      { accessorKey: 'display_name', header: 'Template', size: 160 },
      { accessorKey: 'bot_number', header: 'Bot Number', size: 140 },
      { accessorKey: 'messages_sent', header: 'Sent', size: 80 },
      { accessorKey: 'messages_delivered', header: 'Delivered', size: 100 },
      { accessorKey: 'messages_read', header: 'Read', size: 80 },
      { accessorKey: 'messages_failed', header: 'Failed', size: 80 },
      {
        id: 'delivery_rate',
        header: 'Delivery %',
        accessorFn: (row) => formatRate(row.delivery_rate),
      },
      {
        id: 'read_rate',
        header: 'Read %',
        accessorFn: (row) => formatRate(row.read_rate),
      },
      {
        id: 'cta_ctr',
        header: 'CTA CTR',
        accessorFn: (row) => formatRate(row.cta_ctr),
      },
    ],
    [filters.limit, filters.page]
  );

  const singleColumns = useMemo(
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => filters.page * filters.limit + row.index + 1,
      },
      { accessorKey: 'sent_date', header: 'Sent Date', size: 120 },
      { accessorKey: 'total_users', header: 'Total User', size: 100 },
      {
        accessorKey: 'sent_count',
        header: (
          <Tooltip title="Percentage of messages sent successfully out of total users">
            <span>Sent (%)</span>
          </Tooltip>
        ),
        Cell: ({ row }) => calculatePercentageOfTotal(row.original.sent_count, row.original.total_users),
      },
      {
        accessorKey: 'delivered_count',
        header: (
          <Tooltip title="Percentage of messages delivered successfully out of total users">
            <span>Delivered (%)</span>
          </Tooltip>
        ),
        Cell: ({ row }) =>
          calculatePercentageOfTotal(row.original.delivered_count, row.original.total_users),
      },
      {
        accessorKey: 'read_count',
        header: (
          <Tooltip title="Percentage of messages read out of total users">
            <span>Read (%)</span>
          </Tooltip>
        ),
        Cell: ({ row }) => calculatePercentageOfTotal(row.original.read_count, row.original.total_users),
      },
      {
        accessorKey: 'unique_button_count',
        header: (
          <Tooltip title="Percentage of unique users who clicked a template button">
            <span>UBC (%)</span>
          </Tooltip>
        ),
        Cell: ({ row }) =>
          calculatePercentageOfTotal(row.original.unique_button_count, row.original.total_users),
      },
      ...buttonColumnDefs.map(({ accessorKey, label }) => ({
        accessorKey,
        header: (
          <Tooltip title={`Percentage of users who clicked "${label}"`}>
            <span>{label} (%)</span>
          </Tooltip>
        ),
        Cell: ({ row }) =>
          calculatePercentageOfTotal(row.original[accessorKey], row.original.total_users),
      })),
      {
        id: 'actions',
        accessorKey: 'actions',
        header: 'Action',
        enableSorting: false,
        size: 72,
        Cell: ({ row }) => (
          <InsightsRowActions
            onView={() => navigateToUsers(row.original)}
            onDownload={() => handleDownloadRecipients(row.original)}
          />
        ),
      },
    ],
    [
      buttonColumnDefs,
      filters.limit,
      filters.page,
      handleDownloadRecipients,
      navigateToUsers,
    ]
  );

  const columns = isOrgMode ? orgColumns : singleColumns;

  const onSortingChange = useCallback(
    (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
      if (handleSortingChange) handleSortingChange(next);
    },
    [handleSortingChange, sorting]
  );

  return (
    <Box sx={{ pt: 2 }}>
      <TemplateDataTable
        columns={columns}
        data={paginatedSummaryRows}
        state={{ isLoading, sorting }}
        renderTopToolbar={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableStickyHeader
        enableColumnPinning={!isOrgMode}
        manualSorting={!isOrgMode}
        enablePagination={false}
        onSortingChange={onSortingChange}
        initialState={
          isOrgMode
            ? undefined
            : {
                columnPinning: { right: ['actions'] },
              }
        }
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 340px)' } }}
        muiTableBodyRowProps={
          isOrgMode
            ? ({ row }) => ({
                onClick: () => navigateToTemplate?.(row.original, 'insights'),
                sx: { cursor: 'pointer' },
              })
            : undefined
        }
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              totalCount={totalSummaryCount}
              rowsPerPage={filters.limit}
              page={filters.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
    </Box>
  );
}
